/**
 * Source Map Optimizer
 *
 * This module optimizes source maps generated by the Botpress CLI (`bp dev` and `bp build`)
 * by removing sources that fall outside the agent's src directory.
 *
 * ## Problem
 * The bundled bot includes source maps that reference ALL sources, including:
 * - Agent's own code (~10 files)
 * - All node_modules dependencies (~10,000+ files)
 * - Botpress SDK internals
 *
 * This results in source maps that are 30+ MB, where 99% of the content is from
 * dependencies the user doesn't care about when debugging their agent.
 *
 * ## Solution
 * 1. Parse the source map and identify which sources are inside the agent's src directory
 * 2. Remove all sources outside the agent's src directory
 * 3. Rebuild the mappings (VLQ-encoded segments) with updated indices
 * 4. Save both an optimized .map file AND inline it into the .cjs bundle
 *
 * ## What is VLQ?
 * Variable Length Quantity (VLQ) is a compact encoding format used in source maps.
 * Source maps use VLQ to encode the "mappings" string, which contains information about
 * how each position in the generated code maps back to the original source.
 *
 * Each segment in the mappings can contain up to 5 fields (all VLQ-encoded):
 * 1. Column position in the generated code
 * 2. Source file index (into the sources array)
 * 3. Line number in the original source
 * 4. Column position in the original source
 * 5. Name index (into the names array) - optional
 *
 * The mappings string uses:
 * - `;` to separate lines
 * - `,` to separate segments within a line
 * - Base64 characters to encode VLQ values
 *
 * ## Why This Approach?
 * Simply blanking out source paths isn't enough - the mappings still reference removed
 * sources, and the names array still contains thousands of unused identifiers. We need to:
 * 1. Completely remove unused sources and sourcesContent
 * 2. Remove unused names
 * 3. Rebuild the mappings with correct indices
 *
 * This reduces file size by 90%+ while keeping the source map valid and functional
 * for debugging the agent's own code.
 */

import { readFile, writeFile } from 'fs/promises'
import { resolve, isAbsolute, relative, dirname } from 'path'

export interface SourceMapOptimizeOptions {
  /**
   * Path to the source map file (.cjs.map)
   */
  sourceMapPath: string

  /**
   * Path to the agent's src directory.
   * Sources outside this directory will be removed.
   */
  agentSrcPath: string
}

interface SourceMap {
  version: number
  sources: string[]
  sourcesContent?: (string | null)[]
  mappings: string
  names?: string[]
  file?: string
  sourceRoot?: string
}

/**
 * Decodes VLQ (Variable Length Quantity) encoded value
 */
function decodeVLQ(input: string, pos: number): [number, number] {
  let result = 0
  let shift = 0
  let continuation = true
  let digit: number

  while (continuation) {
    if (pos >= input.length) {
      throw new Error('Unexpected end of VLQ data')
    }

    const char = input.charCodeAt(pos++)
    // Base64 decode
    if (char >= 65 && char <= 90)
      digit = char - 65 // A-Z
    else if (char >= 97 && char <= 122)
      digit = char - 97 + 26 // a-z
    else if (char >= 48 && char <= 57)
      digit = char - 48 + 52 // 0-9
    else if (char === 43)
      digit = 62 // +
    else if (char === 47)
      digit = 63 // /
    else throw new Error('Invalid VLQ character')

    continuation = (digit & 32) !== 0
    digit &= 31
    result += digit << shift
    shift += 5
  }

  const negate = (result & 1) === 1
  result >>= 1
  return [negate ? -result : result, pos]
}

/**
 * Encodes a number as VLQ
 */
function encodeVLQ(value: number): string {
  const base64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'

  let encoded = ''
  let num = value < 0 ? (-value << 1) | 1 : value << 1

  do {
    let digit = num & 31
    num >>>= 5
    if (num > 0) {
      digit |= 32
    }
    encoded += base64[digit]
  } while (num > 0)

  return encoded
}

/**
 * Parses source map mappings and returns segments with source file references
 */
function parseMappings(mappings: string): Array<{
  line: number
  segments: Array<{
    column: number
    sourceIndex?: number
    sourceLine?: number
    sourceColumn?: number
    nameIndex?: number
    encoded: string
  }>
}> {
  const lines: Array<{
    line: number
    segments: Array<{
      column: number
      sourceIndex?: number
      sourceLine?: number
      sourceColumn?: number
      nameIndex?: number
      encoded: string
    }>
  }> = []

  const lineStrings = mappings.split(';')
  let sourceIndex = 0
  let sourceLine = 0
  let sourceColumn = 0
  let nameIndex = 0

  for (let lineNum = 0; lineNum < lineStrings.length; lineNum++) {
    const line = lineStrings[lineNum]
    if (!line) {
      lines.push({ line: lineNum, segments: [] })
      continue
    }

    const segments: Array<{
      column: number
      sourceIndex?: number
      sourceLine?: number
      sourceColumn?: number
      nameIndex?: number
      encoded: string
    }> = []

    let column = 0
    const segmentStrings = line.split(',')

    for (const segmentString of segmentStrings) {
      if (!segmentString) continue

      let pos = 0
      const segment: {
        column: number
        sourceIndex?: number
        sourceLine?: number
        sourceColumn?: number
        nameIndex?: number
        encoded: string
      } = {
        column: 0,
        encoded: segmentString,
      }

      // Column
      const [colDelta, newPos1] = decodeVLQ(segmentString, pos)
      column += colDelta
      segment.column = column
      pos = newPos1

      if (pos < segmentString.length) {
        // Source index
        const [srcDelta, newPos2] = decodeVLQ(segmentString, pos)
        sourceIndex += srcDelta
        segment.sourceIndex = sourceIndex
        pos = newPos2

        if (pos < segmentString.length) {
          // Source line
          const [lineDelta, newPos3] = decodeVLQ(segmentString, pos)
          sourceLine += lineDelta
          segment.sourceLine = sourceLine
          pos = newPos3

          if (pos < segmentString.length) {
            // Source column
            const [colDelta2, newPos4] = decodeVLQ(segmentString, pos)
            sourceColumn += colDelta2
            segment.sourceColumn = sourceColumn
            pos = newPos4

            if (pos < segmentString.length) {
              // Name index
              const [nameDelta, newPos5] = decodeVLQ(segmentString, pos)
              nameIndex += nameDelta
              segment.nameIndex = nameIndex
              pos = newPos5
            }
          }
        }
      }

      segments.push(segment)
    }

    lines.push({ line: lineNum, segments })
  }

  return lines
}

/**
 * Rebuilds mappings string from parsed segments, remapping indices
 */
function rebuildMappings(
  lines: Array<{
    line: number
    segments: Array<{
      column: number
      sourceIndex?: number
      sourceLine?: number
      sourceColumn?: number
      nameIndex?: number
      encoded: string
    }>
  }>,
  sourceIndexMap: Map<number, number>,
  nameIndexMap: Map<number, number>
): string {
  const result: string[] = []
  let prevColumn = 0
  let prevSourceIndex = 0
  let prevSourceLine = 0
  let prevSourceColumn = 0
  let prevNameIndex = 0

  for (let lineNum = 0; lineNum < lines.length; lineNum++) {
    const line = lines[lineNum]
    if (!line) continue

    if (lineNum > 0) {
      result.push(';')
      prevColumn = 0
    }

    const segments: string[] = []

    for (const segment of line.segments) {
      const parts: string[] = []

      // Column delta
      const colDelta = segment.column - prevColumn
      parts.push(encodeVLQ(colDelta))
      prevColumn = segment.column

      if (segment.sourceIndex !== undefined) {
        const newSourceIndex = sourceIndexMap.get(segment.sourceIndex)

        // Skip segments that reference removed sources
        if (newSourceIndex === undefined) {
          continue
        }

        const srcDelta = newSourceIndex - prevSourceIndex
        parts.push(encodeVLQ(srcDelta))
        prevSourceIndex = newSourceIndex

        if (segment.sourceLine !== undefined) {
          const lineDelta = segment.sourceLine - prevSourceLine
          parts.push(encodeVLQ(lineDelta))
          prevSourceLine = segment.sourceLine

          if (segment.sourceColumn !== undefined) {
            const colDelta2 = segment.sourceColumn - prevSourceColumn
            parts.push(encodeVLQ(colDelta2))
            prevSourceColumn = segment.sourceColumn

            if (segment.nameIndex !== undefined) {
              const newNameIndex = nameIndexMap.get(segment.nameIndex)
              if (newNameIndex !== undefined) {
                const nameDelta = newNameIndex - prevNameIndex
                parts.push(encodeVLQ(nameDelta))
                prevNameIndex = newNameIndex
              }
            }
          }
        }
      }

      segments.push(parts.join(''))
    }

    result.push(segments.join(','))
  }

  return result.join('')
}

/**
 * Optimizes a source map by removing sources that fall outside
 * the agent's src directory and rebuilding the mappings.
 *
 * This dramatically reduces the size of the source map (typically 90%+ reduction)
 * without losing valuable debugging information for the agent's own code.
 *
 * The optimized source map is both:
 * 1. Written to the .map file (for external reference)
 * 2. Inlined as base64 into the .cjs bundle (for runtime debugging)
 *
 * We inline the source map because after optimization it's tiny (usually <1MB),
 * which makes it much easier for tools like VSCode and Chrome DevTools to load
 * and use for debugging. Inline source maps eliminate the need for additional
 * HTTP requests or file system lookups, providing instant source mapping.
 *
 * @param options - Configuration options
 * @returns Promise that resolves when the source map has been optimized
 */
export async function optimizeSourceMap(options: SourceMapOptimizeOptions): Promise<void> {
  const { sourceMapPath, agentSrcPath } = options

  // Read and parse the source map
  const sourceMapContent = await readFile(sourceMapPath, 'utf-8')
  const sourceMap: SourceMap = JSON.parse(sourceMapContent)

  // Resolve absolute paths for comparison
  const resolvedAgentSrc = resolve(agentSrcPath)
  const sourceMapDir = resolve(sourceMapPath, '..')

  // Track indices to keep (agent sources only)
  const indicesToKeep = new Set<number>()

  // Find all sources that should be kept (inside agent src directory)
  for (let i = 0; i < sourceMap.sources.length; i++) {
    const source = sourceMap.sources[i]
    if (!source) {
      continue
    }

    // Resolve the source path from the source map's directory
    const resolvedSource = isAbsolute(source) ? resolve(source) : resolve(sourceMapDir, source)

    // Check if this source is inside the agent src directory
    const rel = relative(resolvedAgentSrc, resolvedSource)
    const isInside = !rel.startsWith('..') && !isAbsolute(rel)

    if (isInside) {
      indicesToKeep.add(i)
    }
  }

  // Parse the mappings (VLQ-encoded segments)
  const parsedLines = parseMappings(sourceMap.mappings)

  // Build new sources array and create index mapping
  const newSources: string[] = []
  const newSourcesContent: (string | null)[] = []
  const sourceIndexMap = new Map<number, number>()

  for (const oldIndex of Array.from(indicesToKeep).sort((a, b) => a - b)) {
    const newIndex = newSources.length
    sourceIndexMap.set(oldIndex, newIndex)
    const source = sourceMap.sources[oldIndex]
    if (source !== undefined) {
      // Convert relative paths to absolute for better debugger support
      const absoluteSource = isAbsolute(source) ? source : resolve(dirname(sourceMapPath), source)
      newSources.push(absoluteSource)
    }
    if (sourceMap.sourcesContent) {
      newSourcesContent.push(sourceMap.sourcesContent[oldIndex] || null)
    }
  }

  // Find used names from kept segments
  const usedNameIndices = new Set<number>()
  for (const line of parsedLines) {
    for (const segment of line.segments) {
      if (
        segment.sourceIndex !== undefined &&
        indicesToKeep.has(segment.sourceIndex) &&
        segment.nameIndex !== undefined
      ) {
        usedNameIndices.add(segment.nameIndex)
      }
    }
  }

  // Build new names array and create index mapping
  const newNames: string[] = []
  const nameIndexMap = new Map<number, number>()

  if (sourceMap.names) {
    for (const oldIndex of Array.from(usedNameIndices).sort((a, b) => a - b)) {
      if (oldIndex < sourceMap.names.length) {
        const newIndex = newNames.length
        nameIndexMap.set(oldIndex, newIndex)
        const name = sourceMap.names[oldIndex]
        if (name !== undefined) {
          newNames.push(name)
        }
      }
    }
  }

  // Rebuild mappings with new indices
  const newMappings = rebuildMappings(parsedLines, sourceIndexMap, nameIndexMap)

  // Create optimized source map
  // Note: We don't include sourceRoot since we're using absolute paths
  const optimizedSourceMap: SourceMap = {
    version: sourceMap.version,
    file: sourceMap.file,
    sources: newSources,
    sourcesContent: newSourcesContent.length > 0 ? newSourcesContent : undefined,
    names: newNames.length > 0 ? newNames : undefined,
    mappings: newMappings,
  }

  // Create optimized content
  const optimizedContent = JSON.stringify(optimizedSourceMap)

  if (optimizedContent === sourceMapContent) {
    return
  }

  // 1. Write the optimized source map file
  await writeFile(sourceMapPath, optimizedContent, 'utf-8')

  // 2. Inline the source map into the .cjs file
  const cjsPath = sourceMapPath.replace('.cjs.map', '.cjs')
  let cjsContent = await readFile(cjsPath, 'utf-8')

  // Convert source map to base64
  const base64SourceMap = Buffer.from(optimizedContent, 'utf-8').toString('base64')
  const inlineSourceMap = `//# sourceMappingURL=data:application/json;base64,${base64SourceMap}`

  // Replace existing source map comment with inline version
  cjsContent = cjsContent.replace(/\/\/# sourceMappingURL=.*$/m, inlineSourceMap)

  // If no source map comment was found, append it
  if (!cjsContent.includes('//# sourceMappingURL=')) {
    cjsContent += `\n${inlineSourceMap}`
  }

  // Write the updated .cjs file
  await writeFile(cjsPath, cjsContent, 'utf-8')
}
